import { useEffect, useMemo, useState } from 'react';
import { useLocale } from '../../lib/locale';
import { X, Check } from 'lucide-react';
import { api } from '../../lib/api';

type SortFilter = 'relevance' | 'price-low' | 'price-high' | 'rating';

type DietaryOption = {
  value: string;
  label: string;
  displayOrder: number;
};

type SpiceOption = {
  level: number;
  label: string;
  displayOrder: number;
};

interface MenuFilterModalProps {
  dietaryFilter: string;
  sortBy: SortFilter;
  priceRange: [number, number];
  spicyLevelFilter: number[];
  onClose: () => void;
  onApply: (filters: {
    dietaryFilter: string;
    sortBy: SortFilter;
    priceRange: [number, number];
    spicyLevelFilter: number[];
  }) => void;
}

const FALLBACK_DIETARY: DietaryOption[] = [
  { value: 'all', label: 'All', displayOrder: 0 },
  { value: 'veg', label: 'Vegetarian', displayOrder: 1 },
  { value: 'non-veg', label: 'Non-Veg', displayOrder: 2 },
  { value: 'jain', label: 'Jain', displayOrder: 3 },
];

const FALLBACK_SPICE: SpiceOption[] = [
  { level: 0, label: 'Mild', displayOrder: 0 },
  { level: 1, label: 'Spicy', displayOrder: 1 },
  { level: 2, label: 'Hot', displayOrder: 2 },
  { level: 3, label: 'Extra Hot', displayOrder: 3 },
];

const SORT_OPTIONS: Array<{ value: SortFilter; label: string }> = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'rating', label: 'Rating & Popularity' },
];

const normalizeDietaryValue = (raw: unknown): string => {
  const normalized = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

  if (!normalized) return '';
  if (normalized === 'nonveg') return 'non-veg';
  return normalized;
};

export function MenuFilterModal({
  dietaryFilter,
  sortBy,
  priceRange,
  spicyLevelFilter,
  onClose,
  onApply,
}: MenuFilterModalProps) {
  const { formatMoney } = useLocale();
  const [masterDietaryTags, setMasterDietaryTags] = useState<any[]>([]);
  const [masterSpiceLevels, setMasterSpiceLevels] = useState<any[]>([]);

  const [tempDietary, setTempDietary] = useState(dietaryFilter);
  const [tempSort, setTempSort] = useState<SortFilter>(sortBy);
  const [tempPriceRange, setTempPriceRange] = useState<[number, number]>(priceRange);
  const [tempSpicyLevel, setTempSpicyLevel] = useState<number[]>(spicyLevelFilter);

  useEffect(() => {
    let mounted = true;

    const loadMasterFilters = async () => {
      const [dietaryResponse, spiceResponse] = await Promise.allSettled([
        (api.masterData as any).dietaryTags?.getAll
          ? (api.masterData as any).dietaryTags.getAll()
          : api.masterData.dietaryPreferences.getAll(),
        api.masterData.spiceLevels.getAll(),
      ]);

      if (!mounted) return;

      setMasterDietaryTags(
        dietaryResponse.status === 'fulfilled' && Array.isArray((dietaryResponse.value as any)?.data)
          ? (dietaryResponse.value as any).data
          : []
      );

      setMasterSpiceLevels(
        spiceResponse.status === 'fulfilled' && Array.isArray((spiceResponse.value as any)?.data)
          ? (spiceResponse.value as any).data
          : []
      );
    };

    void loadMasterFilters();

    return () => {
      mounted = false;
    };
  }, []);

  const dietaryOptions = useMemo(() => {
    const rows = (Array.isArray(masterDietaryTags) ? masterDietaryTags : [])
      .map((row: any) => {
        const value = normalizeDietaryValue(row?.code || row?.name || row?.label || row?.title || row?.id);
        const label = String(row?.name || row?.label || row?.title || row?.code || '').trim() || value;
        const displayOrder = Number(row?.displayOrder ?? row?.display_order ?? 0) || 0;
        const isActive = Boolean(row?.isActive ?? row?.is_active ?? true);
        return { value, label, displayOrder, isActive };
      })
      .filter((row) => row.value && row.value !== 'all' && row.isActive)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.label.localeCompare(b.label));

    const unique: DietaryOption[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
      if (seen.has(row.value)) continue;
      seen.add(row.value);
      unique.push({ value: row.value, label: row.label, displayOrder: row.displayOrder });
    }

    return unique.length > 0
      ? [{ value: 'all', label: 'All', displayOrder: 0 }, ...unique]
      : FALLBACK_DIETARY;
  }, [masterDietaryTags]);

  const spiceOptions = useMemo(() => {
    const rows = (Array.isArray(masterSpiceLevels) ? masterSpiceLevels : [])
      .map((row: any) => {
        const level = Number(row?.levelValue ?? row?.level ?? row?.value);
        const label = String(row?.name || row?.label || row?.title || row?.code || '').trim();
        const displayOrder = Number(row?.displayOrder ?? row?.display_order ?? 0) || 0;
        const isActive = Boolean(row?.isActive ?? row?.is_active ?? true);
        return { level, label, displayOrder, isActive };
      })
      .filter((row) => Number.isFinite(row.level) && row.level >= 0 && row.isActive)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.level - b.level)
      .map((row) => ({
        level: row.level,
        label: row.label || `Level ${row.level}`,
        displayOrder: row.displayOrder,
      }));

    return rows.length > 0 ? rows : FALLBACK_SPICE;
  }, [masterSpiceLevels]);

  const toggleSpicyLevel = (level: number) => {
    setTempSpicyLevel((prev) =>
      prev.includes(level) ? prev.filter((item) => item !== level) : [...prev, level]
    );
  };

  const handleApply = () => {
    onApply({
      dietaryFilter: tempDietary,
      sortBy: tempSort,
      priceRange: tempPriceRange,
      spicyLevelFilter: tempSpicyLevel,
    });
    onClose();
  };

  const handleClearAll = () => {
    setTempDietary('all');
    setTempSort('relevance');
    setTempPriceRange([0, 1000]);
    setTempSpicyLevel([]);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2.5 sm:p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-200 p-2.5 sm:p-6">
          <div>
            <h2 className="text-gray-900 text-sm font-bold sm:text-xl">Filter Menu</h2>
            <p className="mt-0.5 text-gray-600 text-[10px] leading-snug sm:mt-1 sm:text-sm">Find exactly what you're craving</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 transition-colors hover:bg-gray-100 sm:p-2">
            <X className="h-4 w-4 text-gray-600 sm:h-6 sm:w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 p-2.5 sm:space-y-6 sm:p-6">
          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold text-gray-900 sm:mb-3 sm:text-base">Dietary Preferences</h3>
            <div className="grid grid-cols-2 gap-1.5 sm:gap-3 md:grid-cols-4">
              {dietaryOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTempDietary(option.value)}
                  className={`rounded-lg border p-2 transition-all sm:rounded-xl sm:border-2 sm:p-4 ${
                    tempDietary === option.value
                      ? 'bg-blue-50 border-blue-500 text-blue-900'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium leading-none sm:text-sm sm:leading-normal">{option.label}</span>
                    {tempDietary === option.value && <Check className="h-3 w-3 sm:h-4 sm:w-4" />}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold text-gray-900 sm:mb-3 sm:text-base">Sort By</h3>
            <div className="grid grid-cols-2 gap-1.5 sm:gap-3">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTempSort(option.value)}
                  className={`rounded-lg border p-2 transition-all sm:rounded-xl sm:border-2 sm:p-4 ${
                    tempSort === option.value
                      ? 'bg-blue-50 border-blue-500 text-blue-900'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium leading-none sm:text-sm sm:leading-normal">{option.label}</span>
                    {tempSort === option.value && <Check className="h-3 w-3 sm:h-4 sm:w-4" />}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold text-gray-900 sm:mb-3 sm:text-base">Spice Level</h3>
            <div className="grid grid-cols-2 gap-1.5 sm:gap-3 md:grid-cols-4">
              {spiceOptions.map((option) => (
                <button
                  key={option.level}
                  onClick={() => toggleSpicyLevel(option.level)}
                  className={`rounded-lg border p-2 transition-all sm:rounded-xl sm:border-2 sm:p-4 ${
                    tempSpicyLevel.includes(option.level)
                      ? 'bg-orange-50 border-orange-500 text-orange-900'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <p className="text-[10px] font-medium leading-none sm:text-sm sm:leading-normal">{option.label}</p>
                    {tempSpicyLevel.includes(option.level) && <Check className="mx-auto mt-1.5 h-3 w-3 sm:mt-2 sm:h-4 sm:w-4" />}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between sm:mb-3">
              <h3 className="text-[11px] font-semibold text-gray-900 sm:text-base">Price Range</h3>
              <span className="text-[10px] font-semibold text-blue-600 sm:text-sm">
                {formatMoney(tempPriceRange[0])} - {formatMoney(tempPriceRange[1])}
              </span>
            </div>
            <div className="space-y-2.5 sm:space-y-4">
              <div>
                <label className="mb-1.5 block text-[10px] text-gray-600 sm:mb-2 sm:text-sm">Minimum Price</label>
                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="50"
                  value={tempPriceRange[0]}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setTempPriceRange([value, Math.max(value, tempPriceRange[1])]);
                  }}
                  className="h-1 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600 sm:h-2"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-[10px] text-gray-600 sm:mb-2 sm:text-sm">Maximum Price</label>
                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="50"
                  value={tempPriceRange[1]}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setTempPriceRange([Math.min(value, tempPriceRange[0]), value]);
                  }}
                  className="h-1 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600 sm:h-2"
                />
              </div>
              <div className="flex justify-between text-[10px] text-gray-500 sm:text-xs">
                <span>{formatMoney(0)}</span>
                <span>{formatMoney(1000)}+</span>
              </div>
            </div>

            <div className="mt-2.5 grid grid-cols-3 gap-1.5 sm:mt-4 sm:gap-2">
              <button
                onClick={() => setTempPriceRange([0, 200])}
                className="rounded-lg bg-gray-100 px-2 py-1.5 text-[10px] font-medium transition-colors hover:bg-blue-50 hover:text-blue-700 sm:px-3 sm:py-2 sm:text-xs"
              >
                Under {formatMoney(200)}
              </button>
              <button
                onClick={() => setTempPriceRange([200, 500])}
                className="rounded-lg bg-gray-100 px-2 py-1.5 text-[10px] font-medium transition-colors hover:bg-blue-50 hover:text-blue-700 sm:px-3 sm:py-2 sm:text-xs"
              >
                {formatMoney(200)}-{formatMoney(500)}
              </button>
              <button
                onClick={() => setTempPriceRange([500, 1000])}
                className="rounded-lg bg-gray-100 px-2 py-1.5 text-[10px] font-medium transition-colors hover:bg-blue-50 hover:text-blue-700 sm:px-3 sm:py-2 sm:text-xs"
              >
                Above {formatMoney(500)}
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 p-2.5 sm:p-6">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <button
              onClick={handleClearAll}
              className="flex-1 rounded-xl border border-gray-300 bg-white px-3 py-2 text-[12px] font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:border-2 sm:px-6 sm:py-3 sm:text-sm"
            >
              Clear All
            </button>
            <button
              onClick={handleApply}
              className="flex-1 rounded-xl bg-blue-600 px-3 py-2 text-[12px] font-medium text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700 sm:px-6 sm:py-3 sm:text-sm"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
